import React, { useState } from 'react';
import moment from 'moment';
import { ErrorText, Modal, PillButton, Row, Stack, Text } from 'library/ui';
import { format12HourTime, formatMonthDay, UpperDaysOfWeek } from 'shared/Dates';
import { Address, IShift } from 'models';
import { useRequestedWorkers } from 'parent-portal/jobs/postJob/hooks/useRequestedWorkers';
import { RequestedWorkerFields } from 'parent-portal/jobs/postJob/components/RequestedWorkerFields';
import { jobRequestErrorMessage } from 'parent-portal/jobs/postJob/postJobApi';
import { updateBusinessAppointment } from './businessAppointmentApi';

function UpdatePreferredWorkerModalContent({
    onClose,
    shift,
    refresh,
}: {
    onClose: () => void;
    shift: IShift;
    refresh: () => void;
}) {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [addresses] = useState<Address[]>([]);
    const [shiftParams] = useState(() => {
        const start = moment(shift.start_date);
        const end = moment(shift.end_date);
        return {
            selectedAddressIds: [shift.ongoing_request.address.id],
            startDate: start.format('YYYY-MM-DD'),
            endDate: end.format('YYYY-MM-DD'),
            selectedDays: [start.format('dddd') as UpperDaysOfWeek],
            times: { start: start.format('HH:mm'), end: end.format('HH:mm'), slots: 1 },
        };
    });
    const workers = useRequestedWorkers({ presets: undefined, addresses, ...shiftParams });

    const existingMatches = (shift.matches ?? []).map((match) => ({
        id: match.matched_worker.id,
        name: `${match.matched_worker.first_name} ${match.matched_worker.last_name}`,
        spotlight: false,
    }));

    function submit() {
        setLoading(true);
        setError('');
        updateBusinessAppointment(shift.id, {
            preferred_sitters: {
                preferred_sitters: workers.requestedWorkers.map((worker) => ({
                    sitter: worker.id,
                    spotlight: worker.spotlight,
                })),
            },
        })
            .then(() => {
                refresh();
                onClose();
            })
            .catch((saveError) => setError(jobRequestErrorMessage(saveError, 'Unable to request workers.')))
            .finally(() => setLoading(false));
    }

    return (
        <Stack gap="md">
            <Text size="md">
                {shift.ongoing_request.headline}, {formatMonthDay(shift.start_date)}{' '}
                {format12HourTime(shift.start_date)}-{format12HourTime(shift.end_date)} (
                {shift.ongoing_request.business_job_type.replace('_', ' ').toLowerCase().capitalize()})
            </Text>
            <RequestedWorkerFields
                pastWorkers={workers.pastWorkers}
                preferredWorkers={workers.preferredWorkers}
                spotlightWorkers={workers.spotlightWorkers}
                requestedWorkers={workers.requestedWorkers}
                onToggleWorker={workers.toggleRequestedWorker}
                existingMatches={existingMatches}
            />
            {error && <ErrorText>{error}</ErrorText>}
            <Row justify="end" fullWidth>
                <PillButton disabled={loading} onClick={submit}>
                    {loading ? 'Submitting…' : 'Submit'}
                </PillButton>
            </Row>
        </Stack>
    );
}

export default function UpdatePreferredWorkerModal({
    open,
    onClose,
    shift,
    refresh,
}: {
    open: boolean;
    onClose: () => void;
    shift: IShift;
    refresh: () => void;
}) {
    if (!open) {
        return null;
    }

    return (
        <Modal title="Request Specific Worker for Shift" onClose={onClose}>
            <UpdatePreferredWorkerModalContent onClose={onClose} shift={shift} refresh={refresh} />
        </Modal>
    );
}
